import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Users, Eye, CalendarDays, ShoppingBag, LogOut, Download, RefreshCw, Clock } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { api } from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "diproses", label: "Diproses" },
  { value: "terbit", label: "Permit Terbit" },
  { value: "selesai", label: "Selesai" },
];

const STATUS_STYLE = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  diproses: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  terbit: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  selesai: "bg-slate-500/15 text-slate-300 border-slate-400/30",
};

const fmt = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const TABS = [
  { key: "statistik", label: "Statistik" },
  { key: "pengunjung", label: "Pengunjung Kembali" },
  { key: "pesanan", label: "Pesanan Masuk" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("statistik");
  const [stats, setStats] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [recent, setRecent] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  const load = useCallback(async () => {
    setLoadingData(true);
    try {
      const [s, v, r, o] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/visitors"),
        api.get("/admin/visits/recent"),
        api.get("/admin/orders"),
      ]);
      setStats(s.data);
      setVisitors(v.data);
      setRecent(r.data);
      setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Gagal memuat data dashboard");
    } finally {
      setLoadingData(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success("Status pesanan diperbarui");
    } catch {
      toast.error("Gagal memperbarui status");
    }
  };

  const exportCsv = () => {
    const header = ["Nama Travel", "Penanggung Jawab", "WhatsApp", "Jumlah Jamaah", "Tgl Keberangkatan", "Status", "Catatan", "Dibuat"];
    const rows = orders.map((o) => [
      o.nama_travel, o.penanggung_jawab, o.whatsapp, o.jumlah_jamaah,
      o.tanggal_keberangkatan || "", o.status, (o.catatan || "").replace(/\n/g, " "), o.created_at,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pesanan-rawdahpass.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center" data-testid="admin-loading">
        <RefreshCw size={24} className="text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-white/8 bg-[#0A0F1D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between">
          <Wordmark />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-slate-400" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="p-2 rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-sky-400/40 transition-colors"
              aria-label="Muat ulang"
            >
              <RefreshCw size={15} className={loadingData ? "animate-spin" : ""} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-white/10 text-slate-300 hover:text-red-300 hover:border-red-400/40 transition-colors"
            >
              <LogOut size={14} /> Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                tab === t.key
                  ? "bg-gradient-to-br from-sky-500 to-sky-700 text-white shadow-lg shadow-sky-950/50"
                  : "border border-white/10 text-slate-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "statistik" && stats && (
          <div data-testid="stats-panel">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: Eye, label: "Total Kunjungan", value: stats.total_visits, tid: "stat-total-visits" },
                { icon: Users, label: "Pengunjung Unik", value: stats.unique_visitors, tid: "stat-unique-visitors" },
                { icon: CalendarDays, label: "Kunjungan Hari Ini", value: stats.today_visits, tid: "stat-today-visits" },
                { icon: ShoppingBag, label: "Pesanan (Pending)", value: `${stats.orders_total} (${stats.orders_pending})`, tid: "stat-orders" },
              ].map((c) => (
                <div key={c.tid} className="glass rounded-2xl p-6" data-testid={c.tid}>
                  <c.icon size={20} className="text-sky-400" />
                  <p className="mt-4 font-mono2 font-semibold text-3xl text-white">{c.value}</p>
                  <p className="mt-1 text-xs text-slate-400">{c.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 glass rounded-2xl p-6" data-testid="stats-chart">
              <p className="text-sm font-medium text-slate-300 mb-6">Kunjungan 7 Hari Terakhir</p>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={stats.series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="visitGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#38BDF8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: "#94A3B8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 13 }}
                    labelStyle={{ color: "#F8FAFC" }}
                    formatter={(v) => [`${v} kunjungan`, ""]}
                  />
                  <Area type="monotone" dataKey="visits" stroke="#38BDF8" strokeWidth={2.5} fill="url(#visitGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-8 glass rounded-2xl overflow-hidden" data-testid="recent-visits-table">
              <p className="px-6 pt-6 pb-4 text-sm font-medium text-slate-300">Kunjungan Terbaru</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-y border-white/8 bg-white/[0.02]">
                      <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Waktu</th>
                      <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Pengunjung</th>
                      <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Halaman</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.slice(0, 20).map((v, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="px-6 py-3 text-slate-400 whitespace-nowrap">{fmt(v.ts)}</td>
                        <td className="px-6 py-3 font-mono2 text-sky-300">{String(v.visitor_id).slice(0, 8)}…</td>
                        <td className="px-6 py-3 text-slate-300">{v.path}</td>
                      </tr>
                    ))}
                    {recent.length === 0 && (
                      <tr><td colSpan={3} className="px-6 py-10 text-center text-slate-500">Belum ada kunjungan tercatat.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "pengunjung" && (
          <div className="glass rounded-2xl overflow-hidden" data-testid="visitors-panel">
            <div className="px-6 pt-6 pb-4">
              <p className="text-sm font-medium text-slate-300">Peringkat Pengunjung — Paling Sering Kembali di Atas</p>
              <p className="text-xs text-slate-500 mt-1">Pengunjung diidentifikasi lewat ID anonim di browser mereka.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-white/8 bg-white/[0.02]">
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">#</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">ID Pengunjung</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Kunjungan</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Pertama</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Terakhir</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map((v, i) => (
                    <tr key={v.visitor_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-6 py-3.5 font-mono2 text-slate-500">{i + 1}</td>
                      <td className="px-6 py-3.5 font-mono2 text-sky-300">{String(v.visitor_id).slice(0, 13)}…</td>
                      <td className="px-6 py-3.5">
                        <span className="font-mono2 font-semibold text-white">{v.visits}x</span>
                      </td>
                      <td className="px-6 py-3.5 text-slate-400 whitespace-nowrap">{fmt(v.first_seen)}</td>
                      <td className="px-6 py-3.5 text-slate-400 whitespace-nowrap">{fmt(v.last_seen)}</td>
                      <td className="px-6 py-3.5">
                        {v.visits > 1 ? (
                          <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300">Kembali</span>
                        ) : (
                          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-500/15 border border-slate-400/20 text-slate-400">Baru</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {visitors.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">Belum ada data pengunjung.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "pesanan" && (
          <div className="glass rounded-2xl overflow-hidden" data-testid="orders-panel">
            <div className="px-6 pt-6 pb-4 flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm font-medium text-slate-300">Pesanan Booking Permit Masuk ({orders.length})</p>
              <button
                onClick={exportCsv}
                data-testid="orders-export-csv-button"
                className="inline-flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-full border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
              >
                <Download size={13} /> Export CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-white/8 bg-white/[0.02]">
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Travel</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">PIC / WA</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Jamaah</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Berangkat</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Masuk</th>
                    <th className="text-left px-6 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`order-row-${o.id}`}>
                      <td className="px-6 py-3.5 text-white font-medium">
                        {o.nama_travel}
                        {o.catatan && <p className="text-xs text-slate-500 font-normal mt-1 max-w-[220px] truncate" title={o.catatan}>{o.catatan}</p>}
                      </td>
                      <td className="px-6 py-3.5 text-slate-300">
                        {o.penanggung_jawab}
                        <a
                          href={`https://wa.me/${String(o.whatsapp).replace(/\D/g, "").replace(/^0/, "62")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block font-mono2 text-xs text-sky-400 hover:text-sky-300 mt-0.5"
                          data-testid={`order-wa-${o.id}`}
                        >
                          {o.whatsapp}
                        </a>
                      </td>
                      <td className="px-6 py-3.5 font-mono2 text-slate-200">{o.jumlah_jamaah}</td>
                      <td className="px-6 py-3.5 text-slate-400 whitespace-nowrap">{o.tanggal_keberangkatan || "-"}</td>
                      <td className="px-6 py-3.5 text-slate-400 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5"><Clock size={12} />{fmt(o.created_at)}</span>
                      </td>
                      <td className="px-6 py-3.5">
                        <select
                          value={o.status}
                          onChange={(e) => updateStatus(o.id, e.target.value)}
                          data-testid={`order-status-select-${o.id}`}
                          className={`text-xs font-medium rounded-full px-3 py-1.5 border bg-[#0A0F1D] focus:outline-none cursor-pointer ${STATUS_STYLE[o.status] || STATUS_STYLE.pending}`}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">Belum ada pesanan masuk.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
